/**
 * Brick Armory — <brick-gif-showcase> (sections/gif-showcase.liquid)
 *
 * Short muted clips that behave like moving photos:
 *  - the video src is attached only when a clip first scrolls into view (lazy);
 *  - clips play while visible and pause when they leave the viewport;
 *  - prefers-reduced-motion: nothing autoplays, the poster stays and the
 *    play button starts the clip on demand;
 *  - every clip has a play/pause toggle; a manual pause is respected.
 */
(() => {
  if (customElements.get('brick-gif-showcase')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class BrickGifShowcase extends HTMLElement {
    connectedCallback() {
      this.clips = Array.from(this.querySelectorAll('[data-clip]'))
        .map((frame) => {
          const video = frame.querySelector('video');
          const toggle = frame.querySelector('[data-clip-toggle]');
          if (!video || !toggle) return null;
          const clip = { frame, video, toggle, userPaused: false, visible: false };
          toggle.addEventListener('click', () => this.toggle(clip));
          video.addEventListener('play', () => this.sync(clip));
          video.addEventListener('pause', () => this.sync(clip));
          return clip;
        })
        .filter(Boolean);

      if (!this.clips.length) return;

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const clip = this.clips.find((c) => c.frame === entry.target);
              if (!clip) return;
              clip.visible = entry.isIntersecting;
              if (clip.visible) {
                if (!reducedMotion.matches && !clip.userPaused) this.play(clip);
              } else {
                clip.video.pause();
              }
            });
          },
          { threshold: 0.35 }
        );
        this.clips.forEach((clip) => this.observer.observe(clip.frame));
      } else if (!reducedMotion.matches) {
        this.clips.forEach((clip) => this.play(clip));
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    attach(clip) {
      const { video } = clip;
      if (video.getAttribute('src') || !video.dataset.src) return;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.src = video.dataset.src;
    }

    play(clip) {
      this.attach(clip);
      const attempt = clip.video.play();
      if (attempt && attempt.catch) attempt.catch(() => this.sync(clip));
    }

    toggle(clip) {
      if (clip.video.paused) {
        clip.userPaused = false;
        this.play(clip);
      } else {
        clip.userPaused = true;
        clip.video.pause();
      }
    }

    sync(clip) {
      const playing = !clip.video.paused;
      clip.frame.toggleAttribute('data-playing', playing);
      const label = playing ? clip.toggle.dataset.labelPause : clip.toggle.dataset.labelPlay;
      if (label) clip.toggle.setAttribute('aria-label', label);
    }
  }

  customElements.define('brick-gif-showcase', BrickGifShowcase);
})();
